"use client";

import { entryInversions, type ChordEntry } from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabels } from "@/hooks/use-notation";
import { MiniPiano } from "./mini-piano";

const VIOLET = "#8b5cf6";
const VIOLET_BRIGHT = "#c4b5fd";
const VIOLET_GLOW = "rgba(139, 92, 246, 0.35)";
const TEXT = "rgba(255, 255, 255, 0.92)";
const TEXT_MUTE = "rgba(255, 255, 255, 0.32)";
const BORDER = "rgba(255, 255, 255, 0.06)";
const SURFACE_LO = "rgba(255, 255, 255, 0.02)";

const FONT_MONO = "var(--font-mono), 'JetBrains Mono', ui-monospace, monospace";

type Props = {
  entry: ChordEntry;
  onClick?: () => void;
  active?: boolean;
};

export function ChordLibraryCard({ entry, onClick, active = false }: Props) {
  const rootInv = entryInversions(entry, 60)[0];
  const lowMidi = Math.floor(rootInv.midis[0] / 12) * 12;
  const preferFlats = entry.root.includes("b");
  const chordLabel = useChordLabel();
  const noteLabels = useNoteLabels();
  const noteList = noteLabels(entry.noteNames).join(" · ");

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "14px 14px",
        background: active ? "rgba(139, 92, 246, 0.08)" : SURFACE_LO,
        border: `1px solid ${active ? VIOLET_GLOW : BORDER}`,
        borderRadius: 10,
        cursor: "pointer",
        position: "relative",
        textAlign: "left",
        boxShadow: active ? "0 0 30px rgba(139, 92, 246, 0.15)" : "none",
        fontFamily: "inherit",
        color: TEXT,
        transition:
          "background-color 150ms, border-color 150ms, box-shadow 150ms",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 5,
          height: 5,
          borderRadius: 99,
          background: VIOLET,
          boxShadow: `0 0 6px ${VIOLET_GLOW}`,
          opacity: 0.7,
        }}
      />

      <div
        style={{
          fontSize: 32,
          fontWeight: 300,
          letterSpacing: "-0.02em",
          color: TEXT,
          lineHeight: 1,
          marginBottom: 8,
        }}
      >
        {chordLabel(entry.symbol)}
      </div>

      <div
        style={{
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: active ? VIOLET_BRIGHT : TEXT_MUTE,
          letterSpacing: "0.08em",
          marginBottom: 14,
        }}
      >
        {noteList}
      </div>

      <MiniPiano
        midis={rootInv.midis}
        lowMidi={lowMidi}
        octaves={2}
        highlightColor={VIOLET}
        preferFlats={preferFlats}
        className="block h-[38px] w-full"
      />
    </button>
  );
}
